/**
 * Transition Rules: EnergyPlus 25.2 to 26.1
 *
 * Based on the official EnergyPlus transition rules from CreateNewIDFUsingRulesV26_1_0.f90
 */

import type {
  VersionTransition,
  ObjectTransitionRule,
} from '../types';
import { createCopyMappings, createShiftedCopyMappings } from '../types';

/**
 * Create the transition definition for 25.2 to 26.1
 */
export function createTransition_25_2_to_26_1(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // ============================================================================
  // Object Transition Rules
  // ============================================================================

  /**
   * AirTerminal:SingleDuct:ParallelPIU:Reheat
   * Field 10 is deleted, fields 11+ shift down by 1
   *
   * Original fields (25.2):
   *   0: Name
   *   1: Availability Schedule Name
   *   2: Maximum Primary Air Flow Rate
   *   3: Maximum Secondary Air Flow Rate
   *   4: Minimum Primary Air Flow Fraction
   *   5: Fan On Flow Fraction
   *   6: Supply Air Inlet Node Name
   *   7: Secondary Air Inlet Node Name
   *   8: Outlet Node Name
   *   9: Reheat Coil Air Inlet Node Name  <- DELETED
   *   10+: remaining fields
   *
   * New fields (26.1):
   *   0-8: same
   *   9+: shifted from 10+
   */
  objectRules.set('AIRTERMINAL:SINGLEDUCT:PARALLELPIU:REHEAT', {
    sourceClass: 'AirTerminal:SingleDuct:ParallelPIU:Reheat',
    targetClass: 'AirTerminal:SingleDuct:ParallelPIU:Reheat',
    fieldMappings: [
      // Copy fields 0-8 as-is
      ...createCopyMappings(0, 9),
      // Field 9 is deleted (marked but no mapping needed)
      { sourceIndex: 9, targetIndex: -1, type: 'delete' },
      // Fields 10+ shift down by 1 (assuming up to 20 fields total)
      ...createShiftedCopyMappings(10, 9, 11),
    ],
  });

  /**
   * AirTerminal:SingleDuct:SeriesPIU:Reheat
   * Field 9 is deleted, fields 10+ shift down by 1
   *
   * Original fields (25.2):
   *   0: Name
   *   1: Availability Schedule Name
   *   2: Maximum Air Flow Rate
   *   3: Maximum Primary Air Flow Rate
   *   4: Minimum Primary Air Flow Fraction
   *   5: Supply Air Inlet Node Name
   *   6: Secondary Air Inlet Node Name
   *   7: Outlet Node Name
   *   8: Reheat Coil Air Inlet Node Name  <- DELETED
   *   9+: remaining fields
   *
   * New fields (26.1):
   *   0-7: same
   *   8+: shifted from 9+
   */
  objectRules.set('AIRTERMINAL:SINGLEDUCT:SERIESPIU:REHEAT', {
    sourceClass: 'AirTerminal:SingleDuct:SeriesPIU:Reheat',
    targetClass: 'AirTerminal:SingleDuct:SeriesPIU:Reheat',
    fieldMappings: [
      // Copy fields 0-7 as-is
      ...createCopyMappings(0, 8),
      // Field 8 is deleted
      { sourceIndex: 8, targetIndex: -1, type: 'delete' },
      // Fields 9+ shift down by 1 (assuming up to 20 fields total)
      ...createShiftedCopyMappings(9, 8, 12),
    ],
  });

  // ============================================================================
  // Report Variable Mappings
  // ============================================================================
  // Note: The CSV file "Report Variables 25-2-0 to 26-1-0.csv" shows 0 mappings
  // for this transition, meaning no report variables were renamed between
  // 25.2 and 26.1. The mappings are kept empty but the structure is in place
  // for future transitions.

  // No variable mappings for this transition
  // variableMappings.set('OLD_VARIABLE_NAME', 'New Variable Name');
  // variableMappings.set('DELETED_VARIABLE', '<DELETE>');

  return {
    fromVersion: { major: 25, minor: 2 },
    toVersion: { major: 26, minor: 1 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}

/**
 * Get all available transitions as a map from version string to transition
 */
export function getAvailableTransitions(): Map<string, VersionTransition> {
  const transitions = new Map<string, VersionTransition>();

  const t25_2_to_26_1 = createTransition_25_2_to_26_1();
  transitions.set('25.2', t25_2_to_26_1);

  return transitions;
}
